import { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';

/**
 * Fetches a relative-performance ("premium") comparison for a publicly-traded
 * company from the Flask `stock-premium-*` endpoints. Three modes:
 *
 *   'peers'  -> stock-premium-industry/<sym>/<period>/?level=auto
 *               company vs a weighted basket of its closest peers.
 *               `data` is already rebased to 100 at the window start.
 *   'snp500' -> stock-premium-snp500/<sym>/<period>/   (vs SPY)
 *   'sector' -> stock-premium-sector/<sym>/<period>/   (vs the sector ETF)
 *               these two return RAW adjusted closes (different scales) — the
 *               caller must normalize before plotting.
 *
 * Every endpoint returns `data: [{ date, stock, benchmark }]` (daily, windowed
 * by `period`) plus benchmark metadata (benchmark_name/group, peers, weights,
 * dropped_peers, etf_blend, benchmark_etf.holdings, ...). The full payload is
 * returned so callers can label and annotate the overlay.
 *
 * Results are cached per-URL at module scope, mirroring useStockSeries, so
 * flipping the overlay mode or remounting never re-fetches. No-ops (null
 * payload) when ticker or mode is falsy — i.e. private companies or "off".
 *
 * Returns { payload, loading, error }.
 */

// Premium mode -> Flask endpoint path segment.
const MODE_ENDPOINT = {
    peers: 'stock-premium-industry',
    snp500: 'stock-premium-snp500',
    sector: 'stock-premium-sector',
};

// key (full URL) -> Promise<payload>
const premiumCache = new Map();

export function useStockPremium(ticker, mode, period = 'all') {
    const { flaskServerAddress } = useAuth();
    const [state, setState] = useState({ payload: null, loading: false, error: null });

    useEffect(() => {
        const endpoint = MODE_ENDPOINT[mode];
        if (!ticker || !endpoint) {
            setState({ payload: null, loading: false, error: null });
            return undefined;
        }

        let cancelled = false;
        const query = mode === 'peers' ? '?level=auto' : '';
        const url = `${flaskServerAddress}${endpoint}/${ticker}/${period}/${query}`;
        setState((s) => ({ ...s, loading: true, error: null }));

        let promise = premiumCache.get(url);
        if (!promise) {
            promise = fetch(url)
                .then((r) => r.json())
                .then((json) => {
                    // Endpoints return { error: "..." } with a non-2xx status for
                    // bad ticker/period or a ticker absent from the peer map.
                    if (!json || json.error) throw new Error(json?.error || 'Failed to fetch premium data');
                    return json;
                })
                .catch((err) => {
                    // Don't cache failures — allow a later retry.
                    premiumCache.delete(url);
                    throw err;
                });
            premiumCache.set(url, promise);
        }

        promise
            .then((payload) => {
                if (!cancelled) setState({ payload, loading: false, error: null });
            })
            .catch((err) => {
                if (!cancelled) setState({ payload: null, loading: false, error: err.message });
            });

        return () => {
            cancelled = true;
        };
    }, [ticker, mode, period, flaskServerAddress]);

    return state;
}

export default useStockPremium;
